"use client";

import { useEffect, useRef } from "react";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { MM_SECTION_MAPPING, MMSectionId } from "@constants/mmSectionTracking";

let lastMmPageViewFire: { key: string; at: number } | null = null;

const buildMmPageViewDedupeKey = (
  pathnameWithSearch: string,
  pageType: string,
  brandName: string | undefined,
  modelName: string | undefined,
  cityName: string | undefined,
  carAvailability: string
) =>
  `${pathnameWithSearch}|${pageType}|view_car_variant_mm|${String(brandName ?? "")}|${String(modelName ?? "")}|${String(cityName ?? "")}|${carAvailability}`;

interface MMSectionsTrackerProps {
  brandName?: string;
  modelName?: string;
  cityName?: string;
  carAvailability?: string;
  pageType?: string;
}

const MMSectionsTracker = ({
  brandName,
  modelName,
  cityName,
  carAvailability = "Yes",
  pageType = "MM page",
}: MMSectionsTrackerProps) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const trackedSectionsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const pathnameWithSearch = `${window.location.pathname}${window.location.search}`;
    const dedupeKey = buildMmPageViewDedupeKey(
      pathnameWithSearch,
      pageType,
      brandName,
      modelName,
      cityName,
      carAvailability
    );
    const now = Date.now();
    if (
      lastMmPageViewFire &&
      lastMmPageViewFire.key === dedupeKey &&
      now - lastMmPageViewFire.at < 1000
    ) {
      return;
    }
    lastMmPageViewFire = { key: dedupeKey, at: now };

    void trackEventWithCommonProps("view", EventType.PAGE_EVENT, {
      pageType: pageType,
      additionalProps: {
        view_type: "view_page",
        view_name: "view_car_variant_mm",
        vehicle_make: brandName,
        vehicle_model: modelName,
        city_name: cityName,
        car_availability: carAvailability,
      },
    });
  }, []);

  useEffect(() => {
    if (!brandName || !modelName) {
      return;
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.id as MMSectionId;
            const sectionName = MM_SECTION_MAPPING[sectionId];

            if (sectionName && !trackedSectionsRef.current.has(sectionId)) {
              trackedSectionsRef.current.add(sectionId);

              trackEventWithCommonProps("view", EventType.PAGE_EVENT, {
                pageType: pageType,
                additionalProps: {
                  view_type: "view_page_section",
                  view_name: "view_mm_section",
                  section_name: sectionName,
                  vehicle_make: brandName,
                  vehicle_model: modelName,
                  city_name: cityName,
                  car_availability: carAvailability,
                },
              });
            }
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: "0px 0px -50px 0px",
      }
    );

    const sectionsToObserve = Object.keys(MM_SECTION_MAPPING);
    sectionsToObserve.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    });

    // Cleanup
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [brandName, modelName, cityName]);

  return null;
};

export default MMSectionsTracker;
